import axios from 'axios';
import { 
  API_BASE_URL, 
  MAX_FILE_SIZE, 
  ALLOWED_FILE_TYPES, 
  ENABLE_MOCK_API,
  IS_DEVELOPMENT,
  IS_PRODUCTION 
} from './env';
import { 
  Job, 
  ApiError, 
  JobInput, 
  ApiResponse,
  JobFormErrors,
  JobResponse,
  Admin
} from '../types';
import { authManager } from '../hooks/useApiAuth';

// Utility functions - File validation, email validation, and date formatting

// Create an axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL.replace(/\/+$/, ''), // Remove trailing slashes
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: IS_DEVELOPMENT ? 30000 : 15000,
  withCredentials: false
});
// Add retry mechanism using a symbol to store retry count
const retrySymbol = Symbol('retry');

// Add request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    // Add auth headers if user is authenticated
    if (authManager.isAuthenticated()) {
      config.headers = {
        ...config.headers,
        ...authManager.getAuthHeader()
      };
    }
    
    if (IS_DEVELOPMENT) {
      const fullUrl = `${config.baseURL}${config.url || ''}`;
      console.log('Request URL:', fullUrl);
      console.log('Request Headers:', config.headers);
    }
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Enhanced response interceptor for error handling with retry logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Log detailed error info in development
    if (IS_DEVELOPMENT) {
      console.error('API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        url: config?.url,
        method: config?.method,
        headers: config?.headers
      });
    }

    // Initialize retry count
    if (config && !config[retrySymbol]) {
      config[retrySymbol] = 0;
    }

    // Retry failed requests (except for 4xx errors) up to 3 times
    if (error.response?.status !== 404 && 
        error.response?.status !== 400 && 
        error.response?.status !== 401 && 
        error.response?.status !== 403 && 
        config && 
        config[retrySymbol] < 3) {
      
      config[retrySymbol] += 1;
      const delay = config[retrySymbol] * 1000; // 1s, 2s, 3s
      
      // Wait for the specified delay
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return apiClient(config);
    }

    return Promise.reject(error);
  }
);

/**
 * Validates if a file's size is within the allowed limit
 * @param file - The file to validate
 * @returns boolean - Whether the file size is valid
 */
export const validateFileSize = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};

/**
 * Validates if a file's type is allowed
 * @param file - The file to validate
 * @returns boolean - Whether the file type is valid
 */
export const validateFileType = (file: File): boolean => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
  return ALLOWED_FILE_TYPES.includes(fileExtension);
};

/**
 * Gets error message for file validation
 * @param file - The file to validate
 * @returns string | null - Error message if validation fails, null otherwise
 */
export const getFileErrorMessage = (file: File): string | null => {
  if (!validateFileSize(file)) {
    return `File size exceeds maximum allowed size (${(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)}MB)`;
  }
  
  if (!validateFileType(file)) {
    return `File type not supported. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}`;
  }
  
  return null;
};

/**
 * Validates an email address
 */
export const validateEmail = (email: string): boolean => {
  const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return regex.test(email);
};

/**
 * Formats a date string into a relative time string (e.g., "2 days ago")
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 30) {
    return `${diffInDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Validate form fields for job creation
 */
export const validateJobForm = (input: Partial<JobInput>): JobFormErrors => {
  const errors: JobFormErrors = {};
  
  // Email validation
  if (!input.referrerEmail) {
    errors.referrerEmail = 'Email is required';
  } else if (!validateEmail(input.referrerEmail)) {
    errors.referrerEmail = 'Invalid email address';
  }
  
  // Job title validation
  if (!input.jobTitle) {
    errors.jobTitle = 'Job title is required';
  }
  
  // Company validation
  if (!input.company) {
    errors.company = 'Company name is required';
  }
  
  // Either job details or file is required
  if (!input.jobDetails && !input.file) {
    errors.jobDetails = 'Either job details or a file must be provided';
  }
  
  // File validation
  if (input.file) {
    const fileError = getFileErrorMessage(input.file);
    if (fileError) {
      errors.file = fileError;
    }
  }
  
  return errors;
};

export const handleApiError = (error: unknown): ApiError => {
  // Basic error type check
  const err = error as { 
    response?: { 
      data?: { message?: string }, 
      status?: number 
    },
    code?: string,
    message?: string 
  };
  
  // Handle authentication errors
  if (err.response?.status === 401) {
    return {
      message: 'Authentication required. Please sign in to continue.',
      status: 401
    };
  }
  
  // Handle network/DNS errors
  if (err.code === 'ERR_NAME_NOT_RESOLVED') {
    return {
      message: 'Could not connect to the server. Please check if the URL is correct.',
      status: 0
    };
  }

  if (err.response?.data?.message) {
    return {
      message: err.response.data.message,
      status: err.response.status || 500
    };
  }
  
  // Handle timeout errors
  if (err.code === 'ECONNABORTED') {
    return {
      message: 'Request timed out. Please try again.',
      status: 408
    };
  }
  
  // Handle network errors
  if (!err.response) {
    return {
      message: `Network error: ${err.message || 'Please check your connection.'}`,
      status: 0
    };
  }
  
  // Fallback error message
  return {
    message: 'An unexpected error occurred',
    status: 500
  };
};

/**
 * Create a type for our API that includes the mock property to fix TypeScript errors
 */
export interface ApiInterface {
  getJobs: (status?: string) => Promise<Job[]>;
  getPendingJobs: () => Promise<Job[]>;
  getJobById: (jobId: string) => Promise<Job>;
  createJob: (jobData: JobInput) => Promise<Job>;
  updateJobStatus: (jobId: string, status: 'active' | 'inactive' | 'pending') => Promise<Job>;
  deleteJob: (jobId: string) => Promise<{ message: string }>;
  admin: {
    checkAdminStatus: (token?: string) => Promise<Admin | null>;
    getAllJobs: () => Promise<Job[]>;
    getPendingJobs: () => Promise<Job[]>;
    updateJobStatus: (jobId: string, status: 'active' | 'inactive' | 'pending') => Promise<Job>;
    deleteJob: (jobId: string) => Promise<{ message: string }>;
  };
  shouldUseMock: () => boolean;
  mock: {
    getJobs: (status?: string) => Promise<Job[]>;
    getPendingJobs: () => Promise<Job[]>;
    createJob: (jobData: JobInput) => Promise<Job>;
    checkAdminStatus: () => Promise<Admin | null>;
    updateJobStatus: (jobId: string, status: 'active' | 'inactive' | 'pending') => Promise<Job>;
    deleteJob: (jobId: string) => Promise<{ message: string }>;
  };
}

// Define mockApi first before using it in the api object
export const mockApi = {
  /**
   * Get mock job listings
   * @returns Promise<Job[]> A list of mock job listings
   */
  getJobs: async (status?: string): Promise<Job[]> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Create mock data with proper types
    const mockJobs: Job[] = [
      {
        uniqueJobId: '1',
        referrerEmail: 'john@techcorp.com',
        jobTitle: 'Senior Software Engineer',
        company: 'TechCorp',
        location: 'San Francisco, CA',
        jobDetails: 'We are looking for a skilled software engineer with 5+ years of experience in React and Node.js.',
        postedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'active',
      },
      {
        uniqueJobId: '2',
        referrerEmail: 'sarah@innovateco.com',
        jobTitle: 'Product Manager',
        company: 'InnovateCo',
        location: 'Remote',
        jobDetails: 'Join our team as a product manager to lead the development of our SaaS platform.',
        postedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'active',
      },
      {
        uniqueJobId: '3',
        referrerEmail: 'david@startupx.com',
        jobTitle: 'UX Designer',
        company: 'StartupX',
        location: 'New York, NY',
        jobDetails: 'Looking for a talented UX designer to improve our product experience.',
        postedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'pending',
      },
      {
        uniqueJobId: '4',
        referrerEmail: 'lisa@techgiant.com',
        jobTitle: 'DevOps Engineer',
        company: 'TechGiant',
        location: 'Remote',
        jobDetails: 'Seeking a DevOps engineer to help scale our infrastructure.',
        postedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'inactive',
      },
      // ...other mock jobs...
    ];
    
    // If status filter is provided, apply it
    if (status) {
      const statusValues = status.split(',');
      return mockJobs.filter(job => statusValues.includes(job.status));
    }
    
    return mockJobs;
  },

  /**
   * Get pending jobs for admin review
   * @returns Promise<Job[]> A list of pending and inactive jobs
   */
  getPendingJobs: async (): Promise<Job[]> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Get all jobs and filter to pending/inactive ones
    const allJobs = await mockApi.getJobs();
    return allJobs.filter(job => job.status === 'pending' || job.status === 'inactive');
  },
  /**
   * Create a mock job
   * @param jobData - The job data to create
   * @returns Promise<Job> - The created job object
   */
  createJob: async (jobData: JobInput): Promise<Job> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Log the job data if in development
    if (IS_DEVELOPMENT) {
      console.log('Creating mock job:', jobData);
    }
    
    // Return a mock response
    return {
      uniqueJobId: Math.random().toString(36).substring(2, 10),
      referrerEmail: jobData.referrerEmail,
      jobTitle: jobData.jobTitle,
      company: jobData.company,
      location: jobData.location,
      jobDetails: jobData.jobDetails,
      fileUrl: jobData.file ? jobData.file.name : undefined,
      postedDate: new Date().toISOString(),
      status: 'pending', // Initial status is pending as per API docs
    };
  },

  /**
   * Mock admin check
   */
  checkAdminStatus: async (): Promise<Admin | null> => {
    // In development, pretend the current user is an admin for testing
    if (IS_DEVELOPMENT) {
      return {
        id: 'mock-admin-id',
        email: 'admin@example.com',
        name: 'Mock Admin',
        role: 'admin',
        created_at: new Date().toISOString()
      };
    }
    return null;
  },
  
  /**
   * Mock admin job update
   */
  updateJobStatus: async (jobId: string, status: 'active' | 'inactive' | 'pending'): Promise<Job> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Find the mock job and update its status
    const mockJobs = await mockApi.getJobs();
    const job = mockJobs.find(j => j.uniqueJobId === jobId);
    
    if (!job) {
      throw new Error(`Job with ID ${jobId} not found`);
    }
    
    // Return the updated job
    return {
      ...job,
      status
    };
  },

  /**
   * Mock delete job
   */
  deleteJob: async (jobId: string): Promise<{ message: string }> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 700));
    
    // Simulate job deletion
    return { message: "Job removed" };
  }
};

// Update the type of our api object
export const api: ApiInterface = {
  getJobs: async (status?: string): Promise<Job[]> => {
    try {
      // Add status filter to the query if provided
      const queryParams = status ? `?status=${status}` : '';
      const response = await apiClient.get<ApiResponse>(`/jobs${queryParams}`);
      
      if (IS_DEVELOPMENT) {
        console.log('Raw API Response:', response);
        console.log('Status:', response.status);
        console.log('Response data:', response.data);
      }

      if (!response.data) {
        throw new Error('No data received from server');
      }

      // Fall back to mock data if response is empty
      if ((!response.data.data || !Array.isArray(response.data.data)) && ENABLE_MOCK_API) {
        console.warn('Empty or invalid response, falling back to mock data');
        return mockApi.getJobs();
      }

      return response.data.data || [];
    } catch (error) {
      console.error('Error in getJobs:', error);
      if (ENABLE_MOCK_API && IS_DEVELOPMENT) {
        console.warn('Error occurred, falling back to mock data');
        return mockApi.getJobs();
      }
      throw handleApiError(error);
    }
  },

  getPendingJobs: async (): Promise<Job[]> => {
    try {
      const response = await apiClient.get<ApiResponse>('/jobs/pending');
      
      if (IS_DEVELOPMENT) {
        console.log('Pending jobs response:', response.data);
      }

      if (!response.data) {
        throw new Error('No data received from server');
      }

      // Fall back to mock data if response is empty
      if ((!response.data.data || !Array.isArray(response.data.data)) && ENABLE_MOCK_API) {
        console.warn('Empty or invalid response from pending jobs endpoint, falling back to mock data');
        return mockApi.getPendingJobs();
      }

      return response.data.data || [];
    } catch (error) {
      console.error('Error in getPendingJobs:', error);
      if (ENABLE_MOCK_API && IS_DEVELOPMENT) {
        console.warn('Error occurred, falling back to mock data');
        return mockApi.getPendingJobs();
      }
      throw handleApiError(error);
    }
  },
  getJobById: async (jobId: string): Promise<Job> => {
    try {
      const response = await apiClient.get<JobResponse>(`/jobs/${jobId}`);
      return response.data.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create a new job posting
   * @param jobData - The job data to create
   * @returns Promise<Job> - The created job object
   */
  createJob: async (jobData: JobInput): Promise<Job> => {
    try {
      // Validate file if provided
      if (jobData.file) {
        const fileError = getFileErrorMessage(jobData.file);
        if (fileError) {
          throw new Error(fileError);
        }
      }
      
      const formData = new FormData();
      // Append text fields
      formData.append('referrerEmail', jobData.referrerEmail);
      formData.append('jobTitle', jobData.jobTitle);
      formData.append('company', jobData.company);
      
      if (jobData.location) {
        formData.append('location', jobData.location);
      }
      
      if (jobData.jobDetails) {
        formData.append('jobDetails', jobData.jobDetails);
      }
      
      if (jobData.file) {
        formData.append('file', jobData.file);
      }
      
      // Add recaptcha token
      if (jobData.recaptchaToken) {
        formData.append('recaptchaToken', jobData.recaptchaToken);
      }
        // Override headers for multipart/form-data
      const response = await apiClient.post<JobResponse>('/jobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.data;
    } catch (error) {
      // If mock API is enabled and we had an error, fall back to mock data
      if (ENABLE_MOCK_API) {
        console.warn('Falling back to mock data for createJob()');
        return mockApi.createJob(jobData);
      }
      throw handleApiError(error);
    }
  },
    /**
   * Update a job's status
   * @param jobId - The ID of the job to update
   * @param status - The new status (active, inactive, or pending)
   * @returns Promise<Job> - The updated job object
   */
  updateJobStatus: async (jobId: string, status: 'active' | 'inactive' | 'pending'): Promise<Job> => {
    try {
      const response = await apiClient.put<JobResponse>(`/jobs/${jobId}`, { status });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Based on the backend implementation, transform the response
      // Cast the response to any to access the backend field names
      const responseData = response.data as any;
      
      // Create a normalized job object
      const updatedJob: Job = {
        uniqueJobId: responseData.id || jobId,
        referrerEmail: responseData.referrer_email || '',
        jobTitle: responseData.job_title || '',
        company: responseData.company || '',
        location: responseData.location || '',
        jobDetails: responseData.job_details || '',
        fileUrl: responseData.file_url,
        postedDate: responseData.created_at || new Date().toISOString(),
        status: responseData.status as 'active' | 'inactive' || status
      };
      
      return updatedJob;
    } catch (error) {
      console.error('Error updating job status:', error);
      throw handleApiError(error);
    }
  },

  deleteJob: async (jobId: string): Promise<{ message: string }> => {
    try {
      const response = await apiClient.delete<ApiResponse>(`/jobs/${jobId}`);
      
      if (IS_DEVELOPMENT) {
        console.log('Delete job response:', response.data);
      }

      return { message: response.data?.message || 'Job removed' };
    } catch (error) {
      console.error('Error deleting job:', error);
      throw handleApiError(error);
    }
  },

  // Admin API functions
  admin: {
    /**
     * Check if the current user is an admin
     * @returns Promise<Admin | null> - The admin data if the user is an admin, null otherwise
     */    checkAdminStatus: async (token?: string): Promise<Admin | null> => {
      try {        const response = await apiClient.get<ApiResponse>('/admin/profile', {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        return response.data.data || null;
      } catch (error) {
        // If error is 404 or 403, user is not an admin
        const err = error as any;
        if (err.response && (err.response.status === 404 || err.response.status === 403)) {
          return null;
        }
        throw handleApiError(error);
      }
    },

    /**
     * Get all jobs for admin management
     * @returns Promise<Job[]> - All jobs in the system
     */
    getAllJobs: async (): Promise<Job[]> => {
      try {        // Based on the backend code, there's no dedicated admin/jobs endpoint
        // We should directly use the regular jobs endpoint instead
        const response = await apiClient.get<ApiResponse>('/jobs');
        
        if (IS_DEVELOPMENT) {
          console.log('Admin jobs response:', response.data);
        }
        
        if (!response.data || !response.data.data) {
          console.warn('Invalid or empty response from jobs endpoint');
          return [];
        }
        
        // The backend returns already transformed data that matches our frontend Job type
        if (Array.isArray(response.data.data)) {
          return response.data.data;
        }
        
        return [];
      } catch (error) {
        // Fall back to mock data in development
        if (ENABLE_MOCK_API && IS_DEVELOPMENT) {
          console.warn('Error fetching admin jobs, using mock data');
          return mockApi.getJobs();
        }
        throw handleApiError(error);
      }
    },    /**
     * Update job status
     * @param jobId - The ID of the job to update
     * @param status - The new status (active, inactive, or pending)
     * @returns Promise<Job> - The updated job
     */
    updateJobStatus: async (jobId: string, status: 'active' | 'inactive' | 'pending'): Promise<Job> => {
      try {
        const response = await apiClient.put<JobResponse>(`/jobs/${jobId}`, { status });
        
        if (!response.data) {
          throw new Error('No data received from server');
        }
        
        // Cast response data to any for flexibility with backend format
        const responseData = response.data as any;
        
        // Transform backend response to match frontend Job type
        const updatedJob: Job = {
          uniqueJobId: responseData.id || jobId,
          referrerEmail: responseData.referrer_email || '',
          jobTitle: responseData.job_title || '',
          company: responseData.company || '',
          location: responseData.location || '',
          jobDetails: responseData.job_details || '',
          fileUrl: responseData.file_url,
          postedDate: responseData.created_at || new Date().toISOString(),
          status: responseData.status as 'active' | 'inactive' || status
        };
        
        return updatedJob;
      } catch (error) {
        console.error('Error updating job status:', error);
        throw handleApiError(error);
      }
    },

    /**
     * Get pending jobs for admin review
     * @returns Promise<Job[]> - All pending jobs in the system
     */
    getPendingJobs: async (): Promise<Job[]> => {
      try {
        const response = await apiClient.get<ApiResponse>('/jobs/pending');
        
        if (IS_DEVELOPMENT) {
          console.log('Admin pending jobs response:', response.data);
        }
        
        if (!response.data || !response.data.data) {
          console.warn('Invalid or empty response from pending jobs endpoint');
          return [];
        }
        
        // The backend returns already transformed data that matches our frontend Job type
        if (Array.isArray(response.data.data)) {
          return response.data.data;
        }
        
        return [];
      } catch (error) {
        // Fall back to mock data in development
        if (ENABLE_MOCK_API && IS_DEVELOPMENT) {
          console.warn('Error fetching pending jobs, using mock data');
          return mockApi.getPendingJobs();
        }
        throw handleApiError(error);
      }
    },

    /**
     * Delete a job
     * @param jobId - The ID of the job to delete
     * @returns Promise<{ message: string }> - Success message
     */
    deleteJob: async (jobId: string): Promise<{ message: string }> => {
      try {
        const response = await apiClient.delete<ApiResponse>(`/jobs/${jobId}`);
        
        if (IS_DEVELOPMENT) {
          console.log('Delete job response:', response.data);
        }

        return { message: response.data?.message || 'Job removed' };
      } catch (error) {
        console.error('Error deleting job:', error);
        throw handleApiError(error);
      }
    },
  },

  // Add shouldUseMock method
  shouldUseMock: () => {
    // Always use mock in development if enabled
    if (IS_DEVELOPMENT && ENABLE_MOCK_API) {
      return true;
    }
    
    // Never use mock in production, even if somehow enabled
    if (IS_PRODUCTION) {
      return false;
    }
    
    return ENABLE_MOCK_API;
  },
  
  // Add mock api methods
  mock: mockApi
};

export default api;

